import React, { useContext, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ButtonTextTransform, Flex, FlexAlignItems, FlexBehavior, FlexJustify, FlexWrap } from 'react-magma-dom';
import { LessonIcon } from '../LessonIcon';
import { VideoModal } from '../../Modal/VideoModal';
import { ErrorDownloadModal } from '../../Modal/ErrorDownloadModal';
import { StyledLessonButton, StyledLessonLink } from '../../Styled';
import { getVideoId, handleDownloadFile } from '../../../utils';
import { LessonPageLink, LessonPageLinkType } from '../../../types/lessons';
import { COLORS } from '../../../constants';
import { DownloadIcon } from '../../../assets/icons/Download';
import { getRedirectLink } from '../../BottomNavigation/RedirectLink';
import { RelatedType } from '../../../types';
import { LessonContext } from '../../../contexts/lessonContext';

type ILessonLink = LessonPageLink & {
  color?: string;
};

export const LessonLink: React.FC<ILessonLink> = props => {
  const { text, type, color } = props;
  const { state } = useContext(LessonContext);
  const { stage, textType } = state;

  const [isLoading, setIsLoading] = useState(false);
  const [isVideoShown, setIsVideoShown] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const allowedLinkTypes = [LessonPageLinkType.exemplar, LessonPageLinkType.iwp];

  if (!allowedLinkTypes.includes(type) && !('url' in props)) {
    return null;
  }

  const openErrorModal = () => {
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const handleOpenVideo = () => {
    setIsVideoShown(true);
  };

  const handleDownload = async () => {
    if (!('url' in props)) return;

    const { url, filename } = props;
    await handleDownloadFile(filename, url, setIsLoading, openErrorModal);
  };

  const handleClick = () => {
    switch (type) {
      case LessonPageLinkType.document:
        return handleDownload();
      case LessonPageLinkType.video:
        return handleOpenVideo();
    }
  };

  const handleUrl = () => {
    if ('exemplarId' in props) {
      const { screen, stage, exemplarId } = props;
      return `/exemplars/${stage.toLowerCase()}/exemplar/${exemplarId}/default/${screen}`;
    }

    if (type === LessonPageLinkType.iwp) {
      return getRedirectLink(stage, textType, RelatedType.proformas);
    }

    return '/';
  };

  const renderLinkContent = () => {
    return (
      <Flex
        behavior={FlexBehavior.container}
        alignItems={FlexAlignItems.center}
        justify={FlexJustify.spaceBetween}
        wrap={FlexWrap.nowrap}
        testId="lesson-link"
      >
        <Flex behavior={FlexBehavior.container} alignItems={FlexAlignItems.center} wrap={FlexWrap.nowrap}>
          {type === LessonPageLinkType.document ? (
            <DownloadIcon
              icon={<LessonIcon icon={type} color={color} />}
              isLoading={isLoading}
              size={25}
              spinner={{ size: 23, color: COLORS.white, width: '25px', height: '25px' }}
            />
          ) : (
            <LessonIcon icon={type} color={color} />
          )}
          <ReactMarkdown skipHtml={false}>{text}</ReactMarkdown>
        </Flex>
        <LessonIcon icon="arrow" />
      </Flex>
    );
  };

  const renderVideoModal = () => {
    if (!('url' in props)) return;

    const { url } = props;

    return <VideoModal id={getVideoId(url)} showModal={isVideoShown} setShowModal={setIsVideoShown} />;
  };

  return (
    <>
      {[LessonPageLinkType.document, LessonPageLinkType.video].includes(type) ? (
        <>
          <StyledLessonButton
            onClick={handleClick}
            disabled={isLoading}
            textTransform={ButtonTextTransform.none}
            bgColor={color}
          >
            {renderLinkContent()}
          </StyledLessonButton>
          {renderVideoModal()}
        </>
      ) : (
        <StyledLessonLink testId="lesson-target-link" to={handleUrl()} target="_blank" isInverse bgColor={color}>
          {renderLinkContent()}
        </StyledLessonLink>
      )}
      {isErrorModalOpen && (
        <ErrorDownloadModal
          mainColor={color || COLORS.seaBlue}
          onCloseClick={closeErrorModal}
          onRetryClick={() => {
            closeErrorModal();
            handleDownload();
          }}
        />
      )}
    </>
  );
};
